import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { useSubFormIsValidSync } from '@hooks/use-sub-form-is-valid-sync';
import { Cell, Grid, Input, StripeInput, VisuallyHidden } from '../../atoms';
import { useReporter } from '../../reporter';
import { ResponsiveImage } from '../../molecules';
import { Flags, ImageContainer, StripeText } from './styles.js';
import visa from './visa.svg';
import mastercard from './mastercard.svg';
import americanexpress from './americanexpress.svg';
import discover from './discover.svg';
import lock from './secure-lock.svg';
import stripeText from './stripe-text.svg';
import { schema } from './schema';
import { billingInformationCompleted } from './events';

export { schema as BillingInformationSchema } from './schema';

export const BillingInformationForm = ({
  labelWithStripe,
  labelNameCard,
  labelCardNumber,
  labelExpirationDate,
  labelCVC,
  placeholderNameCard,
  placeholderCardNumber,
  placeholderExpirationDate,
  placeholderCVC,
}) => {
  const { control, setError, trigger, setValue } = useFormContext();
  const reporter = useReporter();
  const isValid = useSubFormIsValidSync(schema);

  useEffect(() => {
    if (isValid) {
      reporter.tag(billingInformationCompleted());
    }
  }, [isValid, reporter]);

  const onChangeStripe = event => {
    setValue(event.name, event.value, {
      shouldValidate: !event.error,
      shouldDirty: !event.error,
    });
    if (event.error) {
      setError(event.name, { message: event.error.message });
    }
  };

  return (
    <Grid gap={{ row: 4, column: 2 }} columns={2}>
      <Cell columns={2}>
        <StripeText>
          <ImageContainer>
            <ResponsiveImage
              src={lock.src}
              alt=""
              width={lock.width}
              height={lock.height}
            />
          </ImageContainer>
          {labelWithStripe}
          <ImageContainer>
            <ResponsiveImage
              src={stripeText.src}
              alt="Stripe"
              width={stripeText.width}
              height={stripeText.height}
            />
          </ImageContainer>
        </StripeText>
      </Cell>
      <Cell columns={2}>
        <Flags>
          <VisuallyHidden>Accepted Cards:</VisuallyHidden>
          <ImageContainer>
            <ResponsiveImage
              src={americanexpress.src}
              alt="American Express"
              width={americanexpress.width}
              height={americanexpress.height}
            />
          </ImageContainer>
          <ImageContainer>
            <ResponsiveImage src={visa.src} alt="Visa" width={34} height={24} />
          </ImageContainer>
          <ImageContainer>
            <ResponsiveImage
              src={mastercard.src}
              alt="Mastercard"
              width={mastercard.width}
              height={mastercard.height}
            />
          </ImageContainer>
          <ImageContainer>
            <ResponsiveImage
              src={discover.src}
              alt="Discover"
              width={discover.width}
              height={discover.height}
            />
          </ImageContainer>
        </Flags>
      </Cell>
      <Cell columns={2}>
        <Controller
          control={control}
          name="name"
          render={({ field, fieldState: { error } }) => (
            <Input
              {...field}
              labelText={labelNameCard}
              onChange={value => {
                field.onChange(value);
                trigger('name');
              }}
              status={error?.message && 'error'}
              error={error?.message}
              autocomplete="billing name"
            />
          )}
        />
      </Cell>
      <Cell columns={2}>
        <Controller
          control={control}
          name="cardNumber"
          render={({ field, fieldState: { error } }) => (
            <StripeInput
              {...field}
              labelText={labelCardNumber}
              onChange={onChangeStripe}
              status={error?.message && 'error'}
              error={error?.message}
              type="cardNumber"
              placeholder={placeholderCardNumber}
            />
          )}
        />
      </Cell>
      <Cell columns={1}>
        <Controller
          control={control}
          name="cardExpiry"
          render={({ field, fieldState: { error } }) => (
            <StripeInput
              {...field}
              labelText={labelExpirationDate}
              onChange={onChangeStripe}
              status={error?.message && 'error'}
              error={error?.message}
              type="cardExpiry"
              placeholder={placeholderExpirationDate}
            />
          )}
        />
      </Cell>
      <Cell columns={1}>
        <Controller
          control={control}
          name="cardCvc"
          render={({ field, fieldState: { error } }) => (
            <StripeInput
              {...field}
              labelText={labelCVC}
              onChange={onChangeStripe}
              status={error?.message && 'error'}
              error={error?.message}
              type="cardCvc"
              placeholder={placeholderCVC}
            />
          )}
        />
      </Cell>
    </Grid>
  );
};

BillingInformationForm.displayName = 'BillingInformationForm';

BillingInformationForm.propTypes = {
  labelWithStripe: PropTypes.string.isRequired,
  labelNameCard: PropTypes.string.isRequired,
  labelCardNumber: PropTypes.string.isRequired,
  labelExpirationDate: PropTypes.string.isRequired,
  labelCVC: PropTypes.string.isRequired,
  placeholderNameCard: PropTypes.string.isRequired,
  placeholderCardNumber: PropTypes.string.isRequired,
  placeholderExpirationDate: PropTypes.string.isRequired,
  placeholderCVC: PropTypes.string.isRequired,
};
